// PART 1/6 — Imports, component setup, state, hooks

import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Button, Empty, Select } from "antd";
import dayjs from "dayjs";
import {
  Card,
  Col,
  Row,
  Tooltip,
  DatePicker,
  Tabs,
  Switch,
  Table,
  message,
} from "antd";
import ColumnChart from "../charts/ColumnChart";
import DoughNutChart from "../charts/DoughnutChart";
import DistrictMap from "../charts/DistrictMap";
import ProvinceEventsMap from "../charts/ProvinceEventsMap";
import Legend from "../charts/Legend";
import { util } from "../../utils/functions/custom";
import "antd/dist/reset.css";
import { InfoCircleTwoTone } from "@ant-design/icons";
import eventsAPI from "../../services/eventsAnalyticsService";
import lookupAPI from "../../services/commonLookupService";
import { ReportTemplate } from "../custom/ReportTemplate";
import { ReportButtons } from "../custom/ReportButtons";
import { reportTypeEnum } from "../custom/AppEnums";
import { recordError } from "src/utils/logService";
import { DownloadDataAsExcel, CustomDateRangePicker } from "../custom";

import { ReportTemplateCollatedDirect } from "../custom/ReportTemplateCollatedDirect";
import { usePdfGeneration } from "../../hooks/usePdfgeneration";
import LocalMunicipalityMap from "../charts/LocalMunicipalityMap";
import {
  useEventsVenueReportRetrieval,
  useEventsGeoReportRetrieval,
  useEventsIndividualReportRetrieval,
} from "../../hooks/useEventsReportRetrieval"; 

export const EventsReport = (props) => {
  const { filterType } = props;
  const { RangePicker } = DatePicker;
  
  const [initialised, setInitialised] = useState(false);
  // const [ageData, setAgeData] = useState([]);
  // const [raceData, setRaceData] = useState([]);
  // const [genderData, setGenderData] = useState([]);
  // const [disabilityData, setDisabilityData] = useState([]);
  // const [participationTypeData, setParticipationTypeData] = useState([]);
  const [eventsGroupData, setEventsGroupData] = useState([]);
  const [eventsVenueTypeData, setEventsVenueTypeData] = useState([]);
  // const [eventsData, setEventsData] = useState([]);
  const [eventDetails, setEventDetails] = useState({});
  const [showEventDetails, setshowEventDetails] = useState(false);
  const [reportYear, setReportYear] = useState(util.getCurrentRepotYear());
  const [financialYears, setFinancialYears] = useState(
    util.getCurrentFinancialYear(),
  );
  const [filterId, setFilterId] = useState();
  
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // Single-chart modal
  const [show, setShow] = useState(false);
  const [isGeo, setIsGeo] = useState(false);
  
  const [isPrinting, setIsPrinting] = useState(false);
  const [frozenTabList, setFrozenTabList] = useState([]);
  
  // Shared print state
  const [reportOutProps, setReportOutProps] = useState({
    data: [],
    color: "",
    type: "",
    year: 0,
    description: "",
    xLabel: "",
    yLabel: "",
    autoPDF: false,
  });
  
  const { start, end } = util.getLastFinancialYears(1)[0];
  const nStartDate = dayjs(start).format("YYYY-MM-DD");
  const nEndDate = dayjs(end).format("YYYY-MM-DD");
  
  // Use state-based dates (updated by date picker) with FY fallback
  const hookStartDate = startDate || nStartDate;
  const hookEndDate = endDate || nEndDate;
  
  const {
    eventListData: eventListQuery,
    raceData: raceQuery,
    genderData: genderQuery,
    ageData: ageQuery,
    disabilityData: disabilityQuery,
    participationTypeData: participationTypeQuery,
  } = useEventsVenueReportRetrieval(
    hookStartDate,
    hookEndDate,
    filterId,
    filterType,
  );
  
  const { eventListData: individualEventListQuery } =
  useEventsIndividualReportRetrieval(hookStartDate, hookEndDate);
  
  const safeArray = (queryResult) => {
    const d = queryResult?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  };
  
  console.log("FILTER TYPE", filterType)
  const eventsData =
    filterType === "event"
      ? safeArray(individualEventListQuery)
      : safeArray(eventListQuery);

  const ageData = safeArray(ageQuery);
  const raceData = safeArray(raceQuery);
  const genderData = safeArray(genderQuery);
  const disabilityData = safeArray(disabilityQuery);
  const participationTypeData = safeArray(participationTypeQuery);

  const {
    provincialDistributionData: provincialDistributionQuery,
    municipalDistributionData: municipalDistributionQuery,
    provincialParticipationData: provincialParticipationQuery,
    municipalParticipationData: municipalParticipationQuery,
    localMunicipalDistributionData: localMunicipalDistributionQuery,
  } = useEventsGeoReportRetrieval(
    hookStartDate,
    hookEndDate,
    filterId,
    filterType,
  );

  const provincialDistributionData = safeArray(provincialDistributionQuery);
  const municipalDistributionData = safeArray(municipalDistributionQuery);
  const provincialParticipationData = safeArray(provincialParticipationQuery);
  const municipalParticipationData = safeArray(municipalParticipationQuery);
  const localMunicipalDistributionData = safeArray(
    localMunicipalDistributionQuery,
  );

  const { Option } = Select;

  // Static text for Geo maps
  const eventsProvincialDistribution = "Distribution of Events by Province";
  const eventsMunicialDistribution =
    "Distribution of Events by Municipal District";
  const eventsLocalMunicialDistribution =
    "Distribution of Events by Local Municipality";
  const eventsProvincialParticipation =
    "Distribution of Event Participants by Province";
  const eventsMunicipalParticipation =
    "Distribution of Event Participants by Municipal District";
  const monthFormat = "YYYY";

  const {
    ProgressModal,
    PreviewModal,
    getCollatedCallbacks,
    openProgress, // not used (autoOpenProgress=true)
    reset, // call BEFORE starting a new run
  } = usePdfGeneration({
    previewTitle: "Events Report",
    progressTitle: "Generating PDF…",

    defaultFilename:
      props.eventType === "Individual Event" && eventDetails?.name
        ? `EventsReport_${eventDetails.name.replace(/[\/\\:*?"<>|]/g, "_")}.pdf`
        : `EventsReport_${reportYear}.pdf`,

    onPreviewClose: () => {
      setReportOutProps({
        data: [],
        color: "",
        type: "",
        year: 0,
        description: "",
        xLabel: "",
        yLabel: "",
        charts: undefined,
        isMulti: false,
        autoPDF: false,
        expectedCount: 0,
        runId: undefined,
        eventMeta: undefined,
      });
    },
    autoOpenProgress: true,
  });

  // Build the PDF metadata rows from eventDetails (keeps labels consistent)
  function buildEventMetaFromDetails(d) {
    if (!d) return [];
    return [
      { title: "Name", value: d.name },
      { title: "Description", value: d.description },
      { title: "Dates", value: d.dates },
      { title: "Group", value: d.group },
      { title: "Type", value: d.type },
      { title: "Venue", value: d.venue },
      { title: "Organiser", value: d.eventOrganiser },
      { title: "Host organisation", value: d.hostOrganisation },
      { title: "Total number of participants", value: d.totalParticipants },
    ].filter(
      (r) =>
        r.value !== undefined && r.value !== null && `${r.value}`.length > 0,
    );
  }

  const cancelPrintIfActive = useCallback(() => {
    if (isPrinting) {
      setIsPrinting(false);
      setFrozenTabList([]);
      setReportOutProps((prev) => ({
        ...prev,
        autoPDF: false,
        charts: undefined,
        runId: undefined,
        eventMeta: undefined,
      }));
      try {
        reset?.();
      } catch {
        /* ignore */
      }
    }
  }, [isPrinting, reset]);
  // ===== Memoization to stabilize rendering =====

  // stable no-op callbacks
  const noop = useCallback(() => {}, []);

  // memoize event meta for the current event (used for single-chart prints)
  const eventMeta = useMemo(
    () =>
      props.eventType === "Individual Event"
        ? buildEventMetaFromDetails(eventDetails)
        : undefined,
    [props.eventType, eventDetails],
  );

  // memoize select option lists
  const eventsVenueTypeOptions = useMemo(
    () => eventsVenueTypeData.map((e) => ({ key: e.key, value: e.value })),
    [eventsVenueTypeData],
  );
  const eventsGroupOptions = useMemo(
    () => eventsGroupData.map((e) => ({ key: e.key, value: e.value })),
    [eventsGroupData],
  );
  const eventsDataOptions = useMemo(
    () =>
      [...eventsData]
        .sort((a, b) => b.startDate.localeCompare(a.startDate))
        .map((event) => ({
          id: event.id,
          label: `${event.name} (${event.dates})`,
        })),
    [eventsData],
  );

  // Init effect, data loaders (UI-coupled)

  // -------------------------------------
  // INIT
  // -------------------------------------
  useEffect(() => {
    if (!initialised) {
      setFinancialYears(util.getCurrentFinancialYear());
      util.getYearsWithReports(eventsAPI, setFinancialYears, setReportYear);
      setInitialised(true);
    }
    setStartDate(startDate);
    setEndDate(endDate);

    initiateReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, reportYear]);

  useEffect(() => {
    if (financialYears?.length > 0) {
      const fy = financialYears[0]; // latest FY
      const [startYear, endYear] = fy.value.split("/").map(Number);

      const fyStart = `${startYear}-04-01`;
      const fyEnd = `${endYear}-03-31`;

      setStartDate(fyStart);
      setEndDate(fyEnd);
    }
  }, [financialYears]);

  // ensures charts always refresh when year or event/venue/group changes
  useEffect(() => {
    if (!reportYear || filterId === undefined) return;

    // Always update analytics for Tab 1
    updateAnalytics(reportYear, filterId);

    // GEO data is now handled by useEventsGeoReportRetrieval hook
  }, [reportYear, filterId]);

  useEffect(() => {
    if (filterType !== "event" || eventsData.length === 0) return;
    
    // defaulting the the filterid when it's missing (-1) fall back to the most recent event.
    const selectedEvent = eventsData.find((e) => e.id === filterId);
    if (selectedEvent) {
      setEventDetails(selectedEvent);
      return;
    }

    const sorted = [...eventsData].sort((a, b) =>
      b.startDate.localeCompare(a.startDate),
    );
    const firstEvent = sorted[0];
    setFilterId(firstEvent.id);
    setEventDetails(firstEvent);
  }, [filterType, eventsData, filterId]);

  const initiateReportData = (repYear = reportYear) => {
    if (filterType === "venue") getEventsVenueTypeData(repYear);
    else if (filterType === "group") getEventsGroupData(repYear);
    // "event" type: event list is now fetched by useEventsIndividualReportRetrieval hook
  };

  const getEventsVenueTypeData = async (repYear) => {
    try {
      const r = await lookupAPI.getEventTypes();
      if (r?.status === 200) {
        const data = r.data;
        const all = { key: -1, value: "All" };
        data.unshift(all);
        setEventsVenueTypeData(data);
        setFilterId(all.key);
        updateAnalytics(repYear, all.key);
      } else setEventsVenueTypeData([]);
    } catch (e) {
      setEventsVenueTypeData([]);
    }
  };

  const getEventsGroupData = async (repYear) => {
    try {
      const r = await lookupAPI.getEventGroups(true);
      if (r?.status === 200) {
        const data = r.data;
        const all = { key: -1, value: "All" };
        data.unshift(all);
        setEventsGroupData(data);
        setFilterId(all.key);
        updateAnalytics(repYear, all.key);
      } else setEventsGroupData([]);
    } catch (e) {
      setEventsGroupData([]);
    }
  };


  const updateAnalytics = (repYear, fid) => {
    // getAgeReportData(repYear, fid);
    // getGenderData(repYear, fid);
    // getRaceData(repYear, fid);
    // getDisabilityData(repYear, fid);
    // getParticipantsTypeData(repYear, fid);
  };

  // -------------------------------------
  // CHANGE handlers (memoized with useCallback)
  // -------------------------------------
  // const onYearChange = (val) => {
  //   cancelPrintIfActive();
  //   setReportYear(val);
  //   if (filterType === "event") getEventsData(val);
  //   else setReportYear(val); //updateAnalytics(val, filterId);
  // };

  const onChangeEventGroup = (val) => {
    cancelPrintIfActive();
    setFilterId(val);
  };

  const onChangeSwitch = (checked) => {
    setshowEventDetails(checked);
    if (checked && eventsData.length > 0 && filterId == null) {
      const sorted = [...eventsData].sort((a, b) =>
        b.startDate.localeCompare(a.startDate),
      );
      const firstEvent = sorted[0];
      setFilterId(firstEvent.id);
      setEventDetails(firstEvent);
    }
  };

  const onChangeEventId = (id) => {
    cancelPrintIfActive();
    const event = eventsData.find((e) => e.id === id);
    if (!event) return;

    setFilterId(event.id);
    setEventDetails(event);
  };

  const onChangeVenueType = (id) => {
    cancelPrintIfActive();
    setFilterId(id);
  };

  const onTabChange = (key) => {
    // All data fetching is now handled by react-query hooks
    // (useEventsVenueReportRetrieval and useEventsGeoReportRetrieval)
  };

  // Print handlers, print-all, downloads, table columns & data memo

  // -------------------------------------
  // SINGLE CHART PRINT (memoized handlers)
  // -------------------------------------
  const handlePrint = useCallback(
    (color, data, type, desc, title, xLabel = "None") => {
      setReportOutProps((prev) => ({
        ...prev,
        data,
        color,
        type,
        year: reportYear,
        description: desc,
        title,
        xLabel,
        autoPDF: false,
        eventMeta, // memoized above
      }));
      setShow(true);
      setIsGeo(false);
    },
    [reportYear, eventMeta],
  );

  const handleGeoPrint = useCallback(
    (title, desc, data, geoType, dataSource) => {
      setReportOutProps((prev) => ({
        ...prev,
        title,
        description: desc,
        color: undefined,
        data,
        type: geoType,
        year: reportYear,
        dataSource,
        autoPDF: false,
        eventMeta, // memoized above
      }));
      setShow(true);
      setIsGeo(true);
    },
    [reportYear, eventMeta],
  );

  const handlePrintCancel = useCallback(() => {
    setShow(false);
    setIsGeo(false);
    setReportOutProps({
      data: [],
      color: "",
      type: "",
      year: 0,
      description: "",
      xLabel: "",
      yLabel: "",
      autoPDF: false,
    });
  }, []);

  // -------------------------------------
  // PRINT ALL (multi-chart)
  // -------------------------------------
  const handlePrintAll = async () => {
    setFrozenTabList(tabList);
    setIsPrinting(true);
    try {
      reset?.();
    } catch {
      // ignore
    }

    let fid = filterId;
    let selectedEventForRun = eventDetails; // default to current

    if (filterType === "event" && !fid) {
      const r = await eventsAPI.getEvents(reportYear);
      if (r?.status === 200 && r.data.length > 0) {
        const sorted = [...r.data].sort((a, b) =>
          b.startDate.localeCompare(a.startDate),
        );
        fid = sorted[0].id;
        setFilterId(fid);
        setEventDetails(sorted[0]);
        selectedEventForRun = sorted[0]; // use the same event for this run
      }
    }

    // // fetch pure data
    // const [age, race, gender, disability, partType] = await Promise.all([
    //   fetchAgeData(reportYear, fid),
    //   fetchRaceData(reportYear, fid),
    //   fetchGenderData(reportYear, fid),
    //   fetchDisabilityData(reportYear, fid),
    //   fetchParticipationTypeData(reportYear, fid),
    // ]);
    // const { pd, md, pp, mp } = await fetchGeoBundles(
    //   reportYear,
    //   fid,
    //   filterType,
    // );

    // build chart objects
    const charts = [
      {
        color: "demographicPie",
        data: disabilityData,
        type: reportTypeEnum.DOUGHNUT,
        title: "Participants Disability Distribution",
        description:
          "Demographic analysis of events participants, based on disability.",
      },
      {
        color: "raceDoughnut",
        data: raceData,
        type: reportTypeEnum.DOUGHNUT,
        title: "Participants Race Distribution",
        description:
          "Demographic analysis of events participants, based on race.",
      },
      {
        color: "csGender",
        data: genderData,
        type: reportTypeEnum.DOUGHNUT,
        title: "Participants Gender Distribution",
        description:
          "Demographic analysis of events participants, based on gender.",
      },
      {
        color: "ageGroups",
        data: ageData,
        type: reportTypeEnum.COLUMN,
        title: "Participants Age Groups",
        description: "Demographic analysis based on age.",
        xField: "type",
        yField: "value",
      },
      {
        color: "typeDistribution",
        data: participationTypeData,
        type: reportTypeEnum.COLUMN,
        title: "Participants Type Distribution",
        description: "Demographic analysis based on participant type.",
        xField: "type",
        yField: "value",
      },
      // GEO participants
      {
        data: provincialParticipationData,
        type: reportTypeEnum.GEO_PROVINCES,
        title: "Geographic distribution of participants in terms of Provinces",
        description: eventsProvincialParticipation,
        dataSource: "Participants",
      },
      {
        data: municipalParticipationData,
        type: reportTypeEnum.GEO_MUNICIPALS,
        title:
          "Geographic distribution of participants in terms of Municipal boundaries",
        description: eventsMunicipalParticipation,
        dataSource: "Participants",
      },
    ];

    // keep only charts with actual data
    const printableCharts = charts.filter((c) =>
      Array.isArray(c.data) ? c.data.length > 0 : true,
    );

    if (printableCharts.length === 0) {
      message.warning("No data available to print for the current selection.");
      return;
    }

    // Build metadata for this run (only for Individual Event)
    const eventMetaForRun =
      props.eventType === "Individual Event"
        ? buildEventMetaFromDetails(selectedEventForRun)
        : undefined;

    // Unique run id forces fresh mount every run
    const runId = Date.now();

    setReportOutProps({
      data: [],
      color: undefined,
      type: undefined,
      description: "",
      xLabel: "",
      yLabel: "",
      charts: printableCharts,
      year: reportYear,
      isMulti: true,
      autoPDF: true,
      expectedCount: printableCharts.length,
      runId,
      eventMeta: eventMetaForRun, // <-- ensure cover page receives metadata
      reportKind:
        props.eventType === "Individual Event"
          ? "Individual Event Report"
          : props.eventType === "Venue Type"
            ? "Venue Type Report"
            : props.eventType === "Event Group"
              ? "Event Group Report"
              : props.eventType === "Group Event"
                ? "Group Event Report"
                : "Events Report",
      // optional: lets the cover title reflect type
    });
  };

  // -------------------------------------
  // DATA DOWNLOAD
  // -------------------------------------
  const handleDataDownload = (data, filename, chartType) => {
    let csvContent;
    if (chartType === reportTypeEnum.GROUPCOL) {
      const grouped = util.groupDataByType(data, reportYear);
      csvContent =
        "data:text/csv;charset=utf-8," + util.convertArrayDataToCSV(grouped);
      DownloadDataAsExcel(grouped, filename);
    } else {
      csvContent =
        "data:text/csv;charset=utf-8," + util.convertArrayDataToCSV(data);
      DownloadDataAsExcel(data, filename);
    }
  };

  // -------------------------------------
  // TABLE: columns + data (memoized)
  // -------------------------------------
  const columns = useMemo(
    () => [
      { title: "Title", dataIndex: "name" },
      { title: "Details", dataIndex: "age" },
    ],
    [],
  );

  const eventDetailsRows = useMemo(
    () => [
      { key: "1", name: "Name ", age: eventDetails.name },
      { key: "2", name: "Description ", age: eventDetails.description },
      { key: "3", name: "Dates", age: eventDetails.dates },
      { key: "4", name: "Group", age: eventDetails.group },
      { key: "5", name: "Type", age: eventDetails.type },
      { key: "6", name: "Organiser ", age: eventDetails.eventOrganiser },
      { key: "7", name: "Venue", age: eventDetails.venue },
      {
        key: "8",
        name: "Host organisation",
        age: eventDetails.hostOrganisation,
      },
      {
        key: "9",
        name: "Total number of participants ",
        age: eventDetails.totalParticipants,
      },
    ],
    [eventDetails],
  );
  // Tabs, memoized tabList, UI rendering

  // -------------------------------------
  // TABS (Tab1, Tab3, Tab4)
  // -------------------------------------
  function Tab1() {
    return (
      <div className="report-content">
        <Row style={{ paddingBottom: 20 }} gutter={16}>
          {/* Disability */}
          <Col xs={24} md={8}>
            <Card
              title="Participants Disability Distribution"
              bordered={false}
              style={{ height: "auto" }}
              extra={
                <Tooltip title="Demographic analysis of events participants, based on disability.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                disabilityData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "demographicPie",
                        disabilityData,
                        reportTypeEnum.DOUGHNUT,
                        "Demographic analysis of events participants, based on disability.",
                        "Participants Disability Distribution",
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        disabilityData,
                        "Participants Disability Distribution",
                        reportTypeEnum.DOUGHNUT,
                      )
                    }
                  />,
                ]
              }
            >
              {disabilityData.length === 0 ? (
                <Empty />
              ) : (
                <DoughNutChart
                  data={disabilityData}
                  color="demographicPie"
                  type="Disability"
                  onImageSrc={noop}
                  onLegendSrc={noop}
                />
              )}
            </Card>
          </Col>

          {/* Race */}
          <Col xs={24} md={8}>
            <Card
              title="Participants Race Distribution"
              bordered={false}
              style={{ height: "auto" }}
              extra={
                <Tooltip title="Demographic analysis of events participants, based on race.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                raceData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "raceDoughnut",
                        raceData,
                        reportTypeEnum.DOUGHNUT,
                        "Demographic analysis of events participants, based on race.",
                        "Participants Race Distribution",
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        raceData,
                        "Participants Race Distribution",
                        reportTypeEnum.DOUGHNUT,
                      )
                    }
                  />,
                ]
              }
            >
              {raceData.length === 0 ? (
                <Empty />
              ) : (
                <DoughNutChart
                  data={raceData}
                  color="raceDoughnut"
                  type="Race"
                  onImageSrc={noop}
                  onLegendSrc={noop}
                />
              )}
            </Card>
          </Col>

          {/* Gender */}
          <Col xs={24} md={8}>
            <Card
              title="Participants Gender Distribution"
              bordered={false}
              style={{ height: "auto" }}
              extra={
                <Tooltip title="Demographic analysis of events participants, based on gender.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                genderData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "csGender",
                        genderData,
                        reportTypeEnum.DOUGHNUT,
                        "Demographic analysis of events participants, based on gender.",
                        "Participants Gender Distribution",
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        genderData,
                        "Participants Gender Distribution",
                        reportTypeEnum.DOUGHNUT,
                      )
                    }
                  />,
                ]
              }
            >
              {genderData.length === 0 ? (
                <Empty />
              ) : (
                <DoughNutChart
                  data={genderData}
                  color="csGender"
                  type="Gender"
                  onImageSrc={noop}
                  onLegendSrc={noop}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Age + Participant Type */}
        <Row style={{ paddingBottom: 20 }} gutter={16}>
          <Col xs={24} md={12}>
            <Card
              title="Participants Age Groups"
              bordered={false}
              style={{ height: "auto" }}
              extra={
                <Tooltip title="Demographic analysis of events participants, based on age.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                ageData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "ageGroups",
                        ageData,
                        reportTypeEnum.COLUMN,
                        "Demographic analysis of events participants, based on age.",
                        "Participants Age Groups",
                        "type",
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        ageData,
                        "Participants Age Groups",
                        reportTypeEnum.COLUMN,
                      )
                    }
                  />,
                ]
              }
            >
              {ageData.length === 0 ? (
                <Empty />
              ) : (
                <ColumnChart
                  data={ageData}
                  xLabel="type"
                  yLabel="value"
                  color="ageGroups"
                  xAxisLabel="Participants per age group"
                  yAxisLabel="Number per age group"
                  onImageSrc={noop}
                  onLegendSrc={noop}
                />
              )}
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card
              title="Participants Type Distribution"
              bordered={false}
              style={{ height: "auto" }}
              extra={
                <Tooltip title="Demographic analysis of events participants, based on participant type.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                participationTypeData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "typeDistribution",
                        participationTypeData,
                        reportTypeEnum.COLUMN,
                        "Demographic analysis based on participant type.",
                        "Participants Type Distribution",
                        "type",
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        participationTypeData,
                        "Participants Type Distribution",
                        reportTypeEnum.COLUMN,
                      )
                    }
                  />,
                ]
              }
            >
              {participationTypeData.length === 0 ? (
                <Empty />
              ) : (
                <ColumnChart
                  data={participationTypeData}
                  xLabel="type"
                  yLabel="value"
                  color="typeDistribution"
                  xAxisLabel="Participants per type"
                  yAxisLabel="Number per participant type"
                  onImageSrc={noop}
                  onLegendSrc={noop}
                />
              )}
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
  // Tab3 & Tab4, memoized tabList, final render & modals

  function Tab3() {
    return (
      <div className="report-content map">
        <Row span={25} style={{ paddingBottom: 40 }} gutter={16}>
          <Col xs={24}>
            <Card
              title="Geographic distribution of events/activities in terms of Provinces"
              bordered={false}
              className="nysOrigin"
              extra={
                <Tooltip title={eventsProvincialDistribution}>
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                provincialDistributionData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handleGeoPrint(
                        "Geographic distribution of events/activities in terms of Provinces",
                        eventsProvincialDistribution,
                        provincialDistributionData,
                        reportTypeEnum.GEO_PROVINCES,
                        "Events",
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        provincialDistributionData,
                        eventsProvincialDistribution,
                        reportTypeEnum.GEO_PROVINCES,
                      )
                    }
                  />,
                ]
              }
            >
              <ProvinceEventsMap
                data={provincialDistributionData}
                hoverText={eventsProvincialDistribution}
                dataSource="Events"
                onImageSrc={noop}
                onLegendSrc={noop}
              />
              <Legend />
            </Card>
          </Col>
        </Row>

        <Row span={25} style={{ paddingBottom: 30 }} gutter={16}>
          <Col xs={24}>
            <Card
              title="Geographic distribution of events/activities in terms of Municipal boundaries"
              bordered={false}
              className="nysOrigin"
              extra={
                <Tooltip title={eventsMunicialDistribution}>
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                municipalDistributionData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handleGeoPrint(
                        "Geographic distribution of events/activities in terms of Provinces",
                        eventsMunicialDistribution,
                        municipalDistributionData,
                        reportTypeEnum.GEO_MUNICIPALS,
                        "Events",
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        municipalDistributionData,
                        eventsMunicialDistribution,
                        reportTypeEnum.GEO_MUNICIPALS,
                      )
                    }
                  />,
                ]
              }
            >
              <DistrictMap
                data={municipalDistributionData}
                hoverText={eventsMunicialDistribution}
                dataSource="Events"
                onImageSrc={noop}
                onLegendSrc={noop}
              />
              <Legend />
            </Card>
          </Col>
        </Row>

        <Row span={25} style={{ paddingBottom: 20 }} gutter={16}>
          <Col xs={24}>
            <Card
              title="Geographic distribution of events/activities in terms of Local Municipalities"
              bordered={false}
              extra={
                <Tooltip title={eventsLocalMunicialDistribution}>
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                localMunicipalDistributionData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handleGeoPrint(
                        "Geographic distribution of events/activities in terms of Local Municipalities",
                        eventsLocalMunicialDistribution,
                        localMunicipalDistributionData,
                        reportTypeEnum.GEO_LOCAL_MUNICIPALITIES,
                        "Events",
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        localMunicipalDistributionData,
                        eventsLocalMunicialDistribution,
                        reportTypeEnum.GEO_LOCAL_MUNICIPALITIES,
                      )
                    }
                  />,
                ]
              }
            >
              <LocalMunicipalityMap
                data={localMunicipalDistributionData}
                hoverText={eventsLocalMunicialDistribution}
                dataSource="Events"
                onImageSrc={noop}
                onLegendSrc={noop}
              />
              <Legend />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  function Tab4() {
    return (
      <div className="report-content map">
        <Row span={25} style={{ paddingBottom: 20 }} gutter={16}>
          <Col xs={24}>
            <Card
              title="Geographic distribution of participants in terms of Provinces"
              bordered={false}
              extra={
                <Tooltip title={eventsProvincialParticipation}>
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                provincialParticipationData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handleGeoPrint(
                        "Geographic distribution of participants in terms of Provinces",
                        eventsProvincialParticipation,
                        provincialParticipationData,
                        reportTypeEnum.GEO_PROVINCES,
                        "Participants",
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        provincialParticipationData,
                        eventsProvincialParticipation,
                        reportTypeEnum.GEO_PROVINCES,
                      )
                    }
                  />,
                ]
              }
            >
              <ProvinceEventsMap
                data={provincialParticipationData}
                hoverText={eventsProvincialParticipation}
                dataSource="Participants"
                onImageSrc={noop}
                onLegendSrc={noop}
              />
              <Legend />
            </Card>
          </Col>
        </Row>

        <Row span={25} style={{ paddingBottom: 20 }} gutter={16}>
          <Col xs={24}>
            <Card
              title="Geographic distribution of participants in terms of Municipal boundaries"
              bordered={false}
              extra={
                <Tooltip title={eventsMunicipalParticipation}>
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                municipalParticipationData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handleGeoPrint(
                        "Geographic distribution of participants in terms of Municipal boundaries",
                        eventsMunicipalParticipation,
                        municipalParticipationData,
                        reportTypeEnum.GEO_MUNICIPALS,
                        "Participants",
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        municipalParticipationData,
                        eventsMunicipalParticipation,
                        reportTypeEnum.GEO_MUNICIPALS,
                      )
                    }
                  />,
                ]
              }
            >
              <DistrictMap
                data={municipalParticipationData}
                hoverText={eventsMunicipalParticipation}
                dataSource="Participants"
                onImageSrc={noop}
                onLegendSrc={noop}
              />
              <Legend />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  // -------------------------------------
  // TABS LIST (memoized)
  // -------------------------------------
  const tabList = [
    {
      key: "1",
      label: "Participant demographic distribution",
      children: <Tab1 />,
    },
    {
      key: "2",
      label: "Event geographic distribution",
      children: <Tab3 />,
      eventGeo: true,
    },
    {
      key: "3",
      label: "Participant geographic distribution",
      children: <Tab4 />,
    },
  ].filter((item) =>
    props.eventType === "Venue Type"
      ? item
      : props.eventType === "Group Event"
        ? item
        : item.eventGeo === undefined,
  );

  const handleSelectedDateRange = (startDate, endDate) => {};

  // -------------------------------------
  // RENDER
  // -------------------------------------
  return (
    <>
      <div className="report-nys-content">
        <h2 className="report-heading">{`${props.eventType} Report`}</h2>
        <div className="reportYear">
          <Row>
            <Col xs={24} md={11}>
              <div className="reportYearContainer">
                <div
                  style={{
                    margin: "0px auto",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  {/* Financial year:
                  <Select
                    placeholder="Please select financial year"
                    onChange={onYearChange}
                    loading={!financialYears.length}
                    value={reportYear}
                    size="large"
                  >
                    {financialYears?.map((year) => (
                      <Option key={year.id} value={year.id}>
                        {year.value}
                      </Option>
                    ))}
                  </Select> */}
                  Select date range:{" "}
                  <CustomDateRangePicker
                    onApply={(start, end) => {
                      console.log("DATE", start, end);
                      setStartDate(start.format("YYYY-MM-DD"));
                      setEndDate(end.format("YYYY-MM-DD"));
                    }}
                  />
                </div>
              </div>
            </Col>

            {/* Venue Type */}
            {props.eventType === "Venue Type" && (
              <Col xs={24} md={12} lg={11}>
                <div className="report-venue-type">
                  <div
                    style={{
                      margin: "10px auto 0px auto",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    Select event venue type :
                    <Select
                      style={{ minWidth: "65%" }}
                      placeholder="Please select event venue type"
                      onChange={onChangeVenueType}
                      value={filterId}
                    >
                      {eventsVenueTypeOptions.map((o) => (
                        <Option key={o.key} value={o.key}>
                          {o.value}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </Col>
            )}

            {/* Individual Event */}
            {props.eventType === "Individual Event" && (
              <>
                <Col xs={24} md={7} lg={9}>
                  <div className="report-venue-type">
                    <div
                      style={{
                        margin: "10px auto 0px auto",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      Select event:{" "}
                      <Select
                        style={{ width: "65%" }}
                        placeholder="Please select event"
                        onChange={onChangeEventId}
                        value={filterId}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {eventsDataOptions.map((o) => (
                          <Option key={o.id} value={o.id}>
                            {o.label}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </div>
                </Col>
                <Col xs={18} md={4}>
                  <div className="reportMetadata">
                    Show event details: <Switch onChange={onChangeSwitch} />
                  </div>
                </Col>
              </>
            )}

            {/* Event Group */}
            {props.eventType === "Event Group" && (
              <Col xs={24} md={12} lg={11}>
                <div className="report-venue-type">
                  <div
                    style={{
                      margin: "10px auto 0px auto",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    Select event group:{" "}
                    <Select
                      style={{ width: "65%" }}
                      placeholder="Please select event group"
                      onChange={onChangeEventGroup}
                      value={filterId}
                    >
                      {eventsGroupOptions.map((o) => (
                        <Option key={o.key} value={o.key}>
                          {o.value}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </div>

        <br />

        {/* EVENT DETAILS */}
        <Row style={{ display: showEventDetails ? "block" : "none" }}>
          <Col xs={24} md={14} lg={14} offset={5}>
            <Table
              columns={columns}
              dataSource={eventDetailsRows}
              pagination={false}
              size="small"
            />
          </Col>
        </Row>
      </div>

      {/* PRINT ALL BUTTON — only for Individual Event */}
      {props.eventType === "Individual Event" && (
        <Button type="primary" onClick={handlePrintAll}>
          Print All Charts
        </Button>
      )}

      {/* <Col xs={24} md={12}>
        <div style={{ marginTop: 10 }}>
          <strong>Select event date range:</strong>
          <RangePicker
            format="YYYY-MM-DD"
            onChange={(dates) => {
              if (dates) {
                setStartDate(dates[0].format("YYYY-MM-DD"));
                setEndDate(dates[1].format("YYYY-MM-DD"));
              } else {
                setStartDate(null);
                setEndDate(null);
              }
            }}
            size="large"
            style={{ width: "100%", marginTop: 5 }}
          />
        </div>
      </Col> */}

      <div className="space"></div>

      <Tabs
        type="card"
        onChange={onTabChange}
        items={isPrinting ? frozenTabList : tabList}
        className="report-tabs"
      />

      {/* SINGLE-CHART MODAL */}
      {show && !reportOutProps?.autoPDF && (
        <ReportTemplate
          onCancel={handlePrintCancel}
          reportOutProps={reportOutProps}
          show={show}
          isGeo={isGeo}
        />
      )}

      {/* MULTI-PDF ENGINE (fresh mount per run) */}
      {reportOutProps?.autoPDF && (
        <ReportTemplateCollatedDirect
          key={reportOutProps.runId}
          reportOutProps={reportOutProps}
          {...getCollatedCallbacks({
            onReady: () => {
              setIsPrinting(false);
              setFrozenTabList([]);
              // optional: any side-effects after capture completes
            },
            onBlobReady: (url /*, blob */) => {
              // optional: anything when the blob URL is ready
            },
          })}
        />
      )}

      {/* HOOK-DRIVEN MODALS */}
      {ProgressModal}
      {PreviewModal}
    </>
  );
};;